import { createEffect, createMemo, createResource, For, lazy, onCleanup, Show, Suspense } from "solid-js"
import { createStore } from "solid-js/store"
import { useSearchParams } from "@solidjs/router"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import { ButtonV2 } from "@opencode-ai/ui/v2/button-v2"
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogTitle } from "@opencode-ai/ui/v2/dialog-v2"
import { DividerV2 } from "@opencode-ai/ui/v2/divider-v2"
import { Field } from "@opencode-ai/ui/v2/field-v2"
import { TextInputV2 } from "@opencode-ai/ui/v2/text-input-v2"
import { DatoxDashboardReactHost } from "./datox-dashboard-react-host"
import { DatoxRevisionSelect } from "@/components/datox-revision-select"
import {
  createDashboard,
  datoxAgentMode,
  getDashboard,
  deleteDashboard,
  deleteApprovalReviewer,
  listApprovals,
  listArtifactsForPeriod,
  listDashboardDatasets,
  listDashboards,
  listProjects,
  previewDashboardDataset,
  loadDashboardDataset,
  publishDashboard,
  shareDashboardAccess,
  unpublishDashboard,
  updateDashboard,
  type DatoxDashboard,
  type DatoxDashboardDataset,
  type DatoxTransformation,
} from "@/datox/api"
import { useLanguage } from "@/context/language"
import { exportDashboardPdf, prepareReportExport, saveReportExport, type ReportExportContext } from "@/datox/report-exports"
import type { ReportDesignerBridge } from "@/datox/report-designer"
import { REPORT_DESIGNER_ENGLISH } from "@/i18n/datox-reports"
import type { DashboardWidgetReference } from "@/datox/dashboard-sources"

const DashboardSourceDialog = lazy(async () => ({ default: (await import("@/components/dashboard-source-dialog")).DashboardSourceDialog }))

type DashboardWidgetType = "header" | "text" | "title" | "kpi" | "table" | "freeTable" | "bar" | "line" | "scatter" | "pie" | "heatmap" | "multiSeries" | "treemap" | "funnel" | "filter"

type DashboardWidget = {
  id: string
  type: DashboardWidgetType
  title: string
  text?: string
  layout: { x: number; y: number; w: number; h: number }
  data?: { dataset_id?: string; columns?: string[]; metric?: string; label?: string }
}

type DashboardConfig = Record<string, unknown> & {
  title: string
  canvas: { width: number; height: number; orientation: string }
  tabs: Array<{ id: string; name: string; widgets: DashboardWidget[] }>
  design: { theme: string; accent: string }
}

type OutputTable = {
  datasetID: string
  name: string
  headers: string[]
  rows: Array<Record<string, string | number | boolean | null>>
}

type PaletteItem = {
  type: DashboardWidgetType
  label: string
}

const dataBackedWidgetTypes = new Set<DashboardWidgetType>(["kpi", "table", "freeTable", "bar", "line", "scatter", "pie", "heatmap", "multiSeries", "treemap", "funnel", "filter"])

export function DatoxDashboardBuilder(props: { projectID: string; periodID?: string | null; caseID?: string; dashboardID?: string; transformations: DatoxTransformation[]; reportScoped?: boolean; targetID?: string; readOnly?: boolean; previewConfig?: Record<string, unknown>; onExportSaved?: () => void; studio?: ReportDesignerBridge; refreshKey?: number }) {
  const language = useLanguage()
  const [search, setSearch] = useSearchParams<{ export?: string }>()
  const dialog = useDialog()
  const [projects] = createResource(listProjects)
  const readOnly = () => !!props.previewConfig || props.readOnly || !projects()?.some((project) => project.id === props.projectID && project.project_role !== "viewer")
  let autosaveTimer: ReturnType<typeof setTimeout> | undefined
  let lastPersistedConfig = ""
  let loadedDashboardID = ""
  let loadedRevisionKey = ""
  let disposed = false
  onCleanup(() => { disposed = true })
  const [store, setStore] = createStore({
    refresh: 0,
    selectedDashboardID: props.dashboardID ?? "",
    revision: 0,
    dirty: false,
    selectedDatasetID: "",
    paletteSearch: "",
    saving: false,
    error: "",
    config: defaultDashboardConfig(language.t("datox.dashboard.defaultName"), language.t("datox.dashboard.newTabName").replace("{count}", "1")),
    source: undefined as { reference: DashboardWidgetReference; viewingRevision: number; expectedRevision?: number } | undefined,
  })
  // Resource keys depend on scope values, not the identity of a refreshed report.
  const projectID = createMemo(() => props.projectID)
  const periodID = createMemo(() => props.periodID)
  const caseID = createMemo(() => props.caseID)
  const [dashboards, dashboardActions] = createResource(
    () => ({ projectID: projectID(), periodID: periodID(), caseID: caseID(), refresh: store.refresh }),
    async (value) => props.reportScoped && props.dashboardID ? [await getDashboard(value.projectID, props.dashboardID)] : listDashboards(value.projectID, value.periodID, value.caseID),
  )
  const selectedCaseID = createMemo(() => caseID() ?? dashboards.latest?.find((dashboard) => dashboard.id === store.selectedDashboardID)?.case_id ?? undefined)
  const [datasets] = createResource(
    () => ({ projectID: projectID(), periodID: periodID(), caseID: selectedCaseID() }),
    (value) => listDashboardDatasets(value.projectID, value.periodID, value.caseID),
  )
  const [shareArtifacts, shareArtifactActions] = createResource(
    () => ({ projectID: projectID(), periodID: periodID(), caseID: caseID() }),
    (value) => listArtifactsForPeriod(value.projectID, value.periodID, "report", value.caseID),
  )
  const [shareApprovals, shareApprovalActions] = createResource(
    () => ({ projectID: projectID(), periodID: periodID(), caseID: caseID() }),
    (value) => listApprovals(value.projectID, value.periodID, value.caseID),
  )
  const outputTables = createMemo(() => props.transformations.filter((item) => !selectedCaseID() || item.case_id === selectedCaseID()).flatMap((item) => transformationOutputTables(item).filter((table) => {
    if (!props.targetID) return true
    const tables = item.output_json.output_tables
    const index = Number(table.datasetID.split(":").at(-1))
    return Array.isArray(tables) && tables[index]?.target_id === props.targetID
  })))
  const boundTableIDs = createMemo(() => {
    if (store.revision) return ""
    const config = (props.previewConfig ?? store.config) as DashboardConfig
    return [...new Set(config.tabs.flatMap((tab) => tab.widgets.map((widget) => widget.data?.dataset_id)))]
      .filter((id): id is string => !!id && !outputTables().some((table) => table.datasetID === id))
      .sort().join(",")
  })
  const [boundTables] = createResource(
    () => {
      const ids = boundTableIDs()
       return ids ? { projectID: projectID(), dashboardID: store.selectedDashboardID, periodID: periodID(), caseID: selectedCaseID(), ids, refresh: store.refresh + (props.refreshKey || 0) } : undefined
    },
    async (scope) => Promise.all(scope.ids.split(",").map(async (id) => {
      const table = await loadDashboardDataset(scope.projectID, scope.dashboardID, id, scope.periodID, scope.caseID)
      return { datasetID: id, name: table.name, headers: table.columns, rows: table.rows } satisfies OutputTable
    })),
  )
  const widgetTables = createMemo(() => [...outputTables(), ...(boundTables.error ? [] : boundTables.latest ?? [])])
  const datasetOptions = createMemo(() => (datasets.latest?.length ? (datasets.latest ?? []).filter((dataset) => !props.reportScoped || outputTables().some((table) => table.datasetID === dataset.id)) : outputTables().map((table) => ({ id: table.datasetID, name: table.name, source: "Output table", transformation_id: "", table_index: 0, columns: table.headers.map((header) => ({ name: header, type: "string" })), row_count: table.rows.length } satisfies DatoxDashboardDataset))))
  const activeDashboard = createMemo(() => dashboards.latest?.find((dashboard) => dashboard.id === store.selectedDashboardID))
  const [revisionData] = createResource(() => activeDashboard() ? `${props.projectID}:${activeDashboard()!.id}:${store.revision}:${activeDashboard()!.revision ?? 0}` : false,
    async () => {
      const revision = store.revision
      return { requested: revision, dashboard: await getDashboard(props.projectID, store.selectedDashboardID, revision || undefined) }
    })
  const viewedDashboard = () => {
    if (revisionData.error) return undefined
    if (revisionData.latest?.requested !== store.revision) return undefined
    const value = revisionData.latest.dashboard
    return value?.id === store.selectedDashboardID && (!store.revision || value.revision === store.revision) ? value : undefined
  }
  const viewedTables = () => {
    const frozen = viewedDashboard()?.revision_tables
    if (!frozen) return widgetTables()
    if (store.revision) return frozen
    return [...frozen, ...widgetTables().filter((table) => !frozen.some((item) => item.datasetID === table.datasetID))]
  }
  const activeTab = createMemo(() => store.config.tabs[0] ?? defaultDashboardConfig(store.config.title, language.t("datox.dashboard.newTabName").replace("{count}", "1")).tabs[0])
  const selectedDataset = createMemo(() => datasetOptions()[0])
  const dashboardShareReviewers = createMemo(() => {
    const dashboardID = activeDashboard()?.id
    if (!dashboardID) return []
    const artifactIDs = new Set((shareArtifacts.latest ?? []).filter((artifact) => artifact.artifact_metadata?.dashboard_id === dashboardID && artifact.artifact_metadata?.source === "dashboard_share").map((artifact) => artifact.id))
    const artifactsByID = new Map((shareArtifacts.latest ?? []).map((artifact) => [artifact.id, artifact]))
    const reviewers = new Map<string, { approvalIds: string[]; name: string; email: string; clientValue: string; status: string }>()
    ;(shareApprovals.latest ?? []).filter((approval) => artifactIDs.has(approval.report_artifact_id)).forEach((approval) => {
      const artifact = artifactsByID.get(approval.report_artifact_id)
      const reviewerNames = artifact?.artifact_metadata?.reviewer_names
      const reviewerClientValues = artifact?.artifact_metadata?.client_values
      const name = reviewerNames && typeof reviewerNames === "object" ? String((reviewerNames as Record<string, unknown>)[approval.approver_email] ?? "") : ""
      const email = approval.approver_email.toLowerCase()
      const clientValue = reviewerClientValues && typeof reviewerClientValues === "object" ? String((reviewerClientValues as Record<string, unknown>)[email] ?? "") : ""
      const existing = reviewers.get(email)
      reviewers.set(email, {
        approvalIds: [...(existing?.approvalIds ?? []), approval.id],
        name: existing?.name || name,
        email,
        clientValue: existing?.clientValue || clientValue,
        status: existing?.status === "requested" || approval.status === "requested" ? "requested" : approval.status,
      })
    })
    return Array.from(reviewers.values())
  })
  const paletteItems = createMemo<PaletteItem[]>(() => [
    { type: "header", label: language.t("datox.dashboard.widget.header") },
    { type: "text", label: language.t("datox.dashboard.widget.text") },
    { type: "title", label: language.t("datox.dashboard.widget.title") },
    { type: "kpi", label: language.t("datox.dashboard.widget.kpi") },
    { type: "table", label: language.t("datox.dashboard.widget.table") },
    { type: "freeTable", label: language.t("datox.dashboard.widget.freeTable") },
    { type: "bar", label: language.t("datox.dashboard.widget.bar") },
    { type: "line", label: language.t("datox.dashboard.widget.line") },
    { type: "scatter", label: language.t("datox.dashboard.widget.scatter") },
    { type: "pie", label: language.t("datox.dashboard.widget.pie") },
    { type: "heatmap", label: language.t("datox.dashboard.widget.heatmap") },
    { type: "multiSeries", label: language.t("datox.dashboard.widget.multiSeries") },
    { type: "treemap", label: language.t("datox.dashboard.widget.treemap") },
    { type: "funnel", label: language.t("datox.dashboard.widget.funnel") },
    { type: "filter", label: language.t("datox.dashboard.widget.filter") },
  ])
  const filteredPaletteItems = createMemo(() => {
    const query = store.paletteSearch.trim().toLowerCase()
    if (!query) return paletteItems()
    return paletteItems().filter((item) => item.label.toLowerCase().includes(query))
  })

  createEffect(() => {
    const dashboard = activeDashboard() ?? dashboards.latest?.[0]
    if (!dashboard) return
    if (dashboard.id !== store.selectedDashboardID) setStore({ selectedDashboardID: dashboard.id, revision: 0 })
    const view = viewedDashboard()
    if (!view) return
    const key = `${view.id}:${store.revision}:${view.revision ?? 0}`
    if (loadedRevisionKey === key) return
    if (!store.revision && store.dirty && loadedDashboardID === dashboard.id) return
    if (autosaveTimer) clearTimeout(autosaveTimer)
    const config = parseDashboardConfig(view)
    loadedDashboardID = dashboard.id
    loadedRevisionKey = key
    lastPersistedConfig = JSON.stringify(config)
    setStore({ config, dirty: false })
  })

  createEffect(() => {
    const dataset = selectedDataset()
    if (!store.selectedDatasetID && dataset) setStore("selectedDatasetID", dataset.id)
  })

  const openCreateDashboardDialog = () => {
    dialog.show(() => (
      <DialogDatoxDashboardForm
        title={language.t("datox.dashboard.createTitle")}
        submitLabel={language.t("datox.dashboard.create")}
        onSubmit={createNewDashboard}
      />
    ))
  }

  const createNewDashboard = async (name: string) => {
    const title = name.trim()
    if (!title) return
    setStore({ saving: true, error: "" })
    try {
      const dashboard = await createDashboard(props.projectID, {
        case_id: props.caseID,
        period_id: props.periodID,
        name: title,
        draft_config: defaultDashboardConfig(title, language.t("datox.dashboard.newTabName").replace("{count}", "1")),
      })
      await dashboardActions.refetch()
      setStore("selectedDashboardID", dashboard.id)
      setStore("config", parseDashboardConfig(dashboard))
    } catch (error) {
      setStore("error", error instanceof Error ? error.message : language.t("datox.dashboard.error"))
    } finally {
      setStore("saving", false)
    }
  }

  const saveDashboard = async () => {
    const dashboard = activeDashboard()
    if (!dashboard || readOnly() || store.revision || revisionData.loading) return
    const sent = JSON.stringify(store.config)
    setStore({ saving: true, error: "" })
    try {
      await updateDashboard(props.projectID, dashboard.id, { expected_revision: dashboard.revision, name: store.config.title, draft_config: JSON.parse(sent) })
      lastPersistedConfig = sent
      setStore("dirty", JSON.stringify(store.config) !== sent)
      await dashboardActions.refetch()
    } catch (error) {
      setStore("error", error instanceof Error ? error.message : language.t("datox.dashboard.error"))
    } finally {
      setStore("saving", false)
    }
  }

  const openSource = async (widgetID: string) => {
    if (props.previewConfig || store.saving || revisionData.loading) return
    const id = store.selectedDashboardID
    const revision = store.revision
    if (store.dirty) await saveDashboard()
    if (disposed || id !== store.selectedDashboardID || revision !== store.revision || store.dirty) return
    const tab = store.config.tabs.find((tab) => tab.widgets.some((widget) => widget.id === widgetID))
    const widget = tab?.widgets.find((widget) => widget.id === widgetID)
    if (!tab || !widget) return
    setStore("source", { reference: { dashboard_id: id, tab_id: tab.id, widget_id: widgetID, title: widget.title, page_name: tab.name, revision: revision || undefined }, viewingRevision: revision,
      expectedRevision: revision ? undefined : activeDashboard()?.revision_count })
  }
  createEffect(() => {
    if (store.source && (store.source.reference.dashboard_id !== store.selectedDashboardID || store.source.viewingRevision !== store.revision)) setStore("source", undefined)
  })

  const deleteActiveDashboard = async () => {
    const dashboard = activeDashboard()
    if (!dashboard) return
    setStore({ saving: true, error: "" })
    try {
      await deleteDashboard(props.projectID, dashboard.id)
      loadedDashboardID = ""
      lastPersistedConfig = ""
      const nextDashboards = await dashboardActions.refetch()
      const nextDashboard = nextDashboards?.[0]
      setStore("selectedDashboardID", nextDashboard?.id ?? "")
      setStore("config", nextDashboard ? parseDashboardConfig(nextDashboard) : defaultDashboardConfig(language.t("datox.dashboard.defaultName"), language.t("datox.dashboard.newTabName").replace("{count}", "1")))
    } catch (error) {
      setStore("error", error instanceof Error ? error.message : language.t("datox.dashboard.error"))
    } finally {
      setStore("saving", false)
    }
  }
  const publishActiveDashboard = async () => {
    const dashboard = activeDashboard()
    if (!dashboard) return ""
    setStore({ saving: true, error: "" })
    try {
      const published = await publishDashboard(props.projectID, dashboard.id, store.config)
      const publicPath = String(published.dashboard_metadata?.public_path || `/dashboard/public/${published.id}`)
      const url = new URL(publicPath, window.location.origin).toString()
      await navigator.clipboard?.writeText(url).catch(() => undefined)
      await dashboardActions.refetch()
      return url
    } catch (error) {
      setStore("error", error instanceof Error ? error.message : language.t("datox.dashboard.error"))
      return ""
    } finally {
      setStore("saving", false)
    }
  }
  const unpublishActiveDashboard = async () => {
    const dashboard = activeDashboard()
    if (!dashboard) return
    setStore({ saving: true, error: "" })
    try {
      await unpublishDashboard(props.projectID, dashboard.id)
      await dashboardActions.refetch()
    } catch (error) {
      setStore("error", error instanceof Error ? error.message : language.t("datox.dashboard.error"))
    } finally {
      setStore("saving", false)
    }
  }
  const shareActiveDashboardAccess = async (reviewers: Array<{ name?: string; email: string; clientValue?: string }>, message?: string) => {
    const dashboard = activeDashboard()
    if (!dashboard) return ""
    const emails = reviewers.map((reviewer) => reviewer.email.trim()).filter(Boolean)
    const reviewerNames = Object.fromEntries(reviewers.map((reviewer) => [reviewer.email.trim().toLowerCase(), reviewer.name?.trim() ?? ""]).filter(([email]) => !!email))
    const clientValues = Object.fromEntries(reviewers.map((reviewer) => [reviewer.email.trim().toLowerCase(), reviewer.clientValue?.trim() ?? ""]).filter(([email]) => !!email))
    setStore({ saving: true, error: "" })
    try {
      const response = await shareDashboardAccess(props.projectID, dashboard.id, { approver_emails: emails, reviewer_names: reviewerNames, client_values: clientValues, message, published_config: store.config })
      await dashboardActions.refetch()
      await shareArtifactActions.refetch()
      await shareApprovalActions.refetch()
      return new URL(response.url, window.location.origin).toString()
    } catch (error) {
      setStore("error", error instanceof Error ? error.message : language.t("datox.dashboard.error"))
      return ""
    } finally {
      setStore("saving", false)
    }
  }
  const removeDashboardShareReviewer = async (approvalIds: string[]) => {
    setStore({ saving: true, error: "" })
    try {
      await Promise.all(approvalIds.map((approvalID) => deleteApprovalReviewer(props.projectID, approvalID)))
      await shareArtifactActions.refetch()
      await shareApprovalActions.refetch()
    } catch (error) {
      setStore("error", error instanceof Error ? error.message : language.t("datox.dashboard.error"))
    } finally {
      setStore("saving", false)
    }
  }

  const prepareExport = async (config: Record<string, unknown>): Promise<ReportExportContext> => {
    const dashboard = activeDashboard()
    if (!dashboard || store.saving || readOnly()) throw new Error("Report unavailable")
    if (autosaveTimer) clearTimeout(autosaveTimer)
    setStore("saving", true)
    try {
      if (store.revision) {
        const context = await prepareReportExport(props.projectID, dashboard.id, store.revision)
        return { ...context, scopeLabel: language.t("datox.report.exportScope", { client: String(context.context.client_id || language.t("datox.report.unassigned")), date: String(context.context.as_of || language.t("datox.report.unassigned")), version: context.revision }) }
      }
      const saved = await updateDashboard(props.projectID, dashboard.id, { expected_revision: dashboard.revision, name: String(config.title || dashboard.name), draft_config: config })
      if (!saved.revision) throw new Error(language.t("datox.report.exportFailed"))
      lastPersistedConfig = JSON.stringify(config)
      setStore({ dirty: JSON.stringify(store.config) !== lastPersistedConfig })
      const context = await prepareReportExport(props.projectID, dashboard.id, saved.revision)
      await dashboardActions.refetch()
      return { ...context, scopeLabel: language.t("datox.report.exportScope", { client: String(context.context.client_id || language.t("datox.report.unassigned")), date: String(context.context.as_of || language.t("datox.report.unassigned")), version: context.revision }) }
    } finally { setStore("saving", false) }
  }
  const persistExport = async (context: ReportExportContext, pages: string[], file: Blob) => {
    await saveReportExport(props.projectID, context.dashboard_id, context, pages, file, crypto.randomUUID())
    props.onExportSaved?.()
  }
  createEffect(() => { props.studio?.onSaveState({ dirty: store.dirty, saving: store.saving, error: store.error }) })
  createEffect(() => {
    if (!props.refreshKey) return
    void dashboardActions.refetch()
  })

  createEffect(() => {
    if (readOnly() || store.revision || revisionData.loading || store.saving || store.error) {
      if (autosaveTimer) clearTimeout(autosaveTimer)
      return
    }
    const dashboard = activeDashboard()
    if (!dashboard || dashboard.id !== loadedDashboardID) return
    const configJSON = JSON.stringify(store.config)
    if (!lastPersistedConfig || configJSON === lastPersistedConfig) return
    if (autosaveTimer) clearTimeout(autosaveTimer)
    autosaveTimer = setTimeout(() => {
      void saveDashboard()
    }, 700)
  })
  onCleanup(() => { if (autosaveTimer) clearTimeout(autosaveTimer) })

  const addWidget = (type: DashboardWidgetType) => {
    const widgets = activeTab().widgets
    const datasetID = store.selectedDatasetID || selectedDataset()?.id || ""
    const table = datasetID ? tableForDataset(widgetTables(), datasetID) : undefined
    const metric = firstNumericColumn(table) ?? table?.headers[0]
    const label = table?.headers[0]
    const widget = defaultWidget(type, widgets.length, datasetID, metric, label, language.t)
    setStore("config", "tabs", 0, "widgets", [...widgets, widget])
  }

  const removeWidget = (widgetID: string) => {
    setStore("config", "tabs", 0, "widgets", activeTab().widgets.filter((widget) => widget.id !== widgetID))
  }

  return (
    <div class="flex min-h-0 min-w-0 max-w-full flex-1 flex-col" classList={{ "designer-builder": !!props.studio }}>
    <Show when={activeDashboard()}>{(dashboard) => <div class="flex flex-wrap items-center gap-3 border-b border-v2-border-border-base p-3" classList={{ "designer-revisionbar": !!props.studio }}>
      <Show when={store.revision}><select class="rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-3 py-2" aria-label={language.t("datox.dashboard.title")}
        value={store.selectedDashboardID} onChange={(event) => setStore({ selectedDashboardID: event.currentTarget.value, revision: 0 })}>
        <For each={dashboards.latest ?? []}>{(item) => <option value={item.id}>{item.name}</option>}</For>
      </select></Show>
      <DatoxRevisionSelect projectID={props.projectID} outputID={dashboard().id} kind="dashboard" value={store.revision}
        appearance={props.studio && datoxAgentMode() ? "menu" : undefined}
        refreshKey={`${viewedDashboard()?.revision_count ?? dashboard().revision_count ?? 0}`}
        disabled={store.saving || store.dirty || revisionData.loading} onChange={(revision) => setStore("revision", revision)} />
      <button type="button" class="rounded-md border border-v2-border-border-base px-3 py-2 text-12-medium"
        disabled={store.saving || store.dirty || dashboards.loading} onClick={() => void dashboardActions.refetch()}>{language.t("datox.revisions.refresh")}</button>
      <Show when={store.error && store.dirty}><button type="button" class="rounded-md border border-v2-border-border-base px-3 py-2 text-12-medium"
        disabled={store.saving} onClick={() => void saveDashboard()}>{language.t("datox.revisions.retrySave")}</button></Show>
    </div>}</Show>
    <Show when={revisionData.error}><p role="alert" class="p-4">{language.t("datox.revisions.error")}</p></Show>
    <div data-testid="report-canvas" class="min-h-0 min-w-0 flex-1 overflow-auto" classList={{ "designer-builder-host": !!props.studio }}>
    <Show when={!dashboards.loading && (dashboards.latest?.length ?? 0) === 0} fallback={<Show when={viewedDashboard()} fallback={<p role="status" class="p-4">{language.t("datox.revisions.loading")}</p>}><DatoxDashboardReactHost
      dashboards={(dashboards.latest ?? []).map((dashboard) => dashboard.id === store.selectedDashboardID ? { ...dashboard, name: store.config.title } : dashboard)}
      datasets={store.revision ? viewedDashboard()?.revision_datasets ?? [] : datasetOptions()}
      outputTables={viewedTables()}
      selectedDashboardID={store.selectedDashboardID}
      selectedDatasetID={store.selectedDatasetID}
      config={props.previewConfig ?? store.config}
      analytics={store.revision ? undefined : { projectID: props.projectID, dashboardID: store.selectedDashboardID, revision: activeDashboard()?.revision_count }}
      onExplainSource={props.previewConfig ? undefined : openSource}
      sourceDisabled={store.saving || revisionData.loading}
      readOnly={readOnly() || !!store.revision}
      scoped={props.reportScoped}
      autoExport={search.export === "1"}
      onAutoExportHandled={() => setSearch({ export: undefined }, { replace: true })}
      studio={props.studio}
      onPrepareExport={prepareExport}
      onSaveExport={persistExport}
      onExportDashboardPdf={(context, html, filename) => exportDashboardPdf(props.projectID, context.dashboard_id, context.revision, html, filename)}
      saving={store.saving}
      loading={dashboards.loading && !viewedDashboard()}
      error={store.error || (boundTables.error ? language.t("datox.designer.datasetLoadIncomplete") : "")}
      onCreateDashboard={createNewDashboard}
      onDeleteDashboard={deleteActiveDashboard}
      onPublishDashboard={publishActiveDashboard}
      onUnpublishDashboard={unpublishActiveDashboard}
      onShareAccess={shareActiveDashboardAccess}
      onRemoveShareReviewer={removeDashboardShareReviewer}
      dashboardShareReviewers={dashboardShareReviewers()}
      onSelectDashboard={(dashboardID) => setStore({ selectedDashboardID: dashboardID, revision: 0 })}
      onSelectDataset={(datasetID) => setStore("selectedDatasetID", datasetID)}
      onConfigChange={(config) => { if (!store.revision) setStore({ config: config as DashboardConfig, dirty: JSON.stringify(config) !== lastPersistedConfig, error: "" }) }}
      labels={{
        designer: Object.fromEntries((Object.keys(REPORT_DESIGNER_ENGLISH) as (keyof typeof REPORT_DESIGNER_ENGLISH)[]).map((key) => [key.replace("datox.designer.", ""), language.t(key)])),
        notConnected: language.t("datox.report.notConnected"),
        noValues: language.t("datox.report.noValues"),
        exportPages: language.t("datox.report.exportPages"),
        exportIntro: language.t("datox.report.exportIntro"),
        exportSelectAll: language.t("datox.report.exportSelectAll"),
        exportClearSelection: language.t("datox.report.exportClearSelection"),
        exportSelectionSummary: (selected: number, total: number) => language.t("datox.report.exportSelectionSummary", { selected, total }),
        exportPageNumber: (number: number) => language.t("datox.report.exportPageNumber", { number }),
        exportDetails: language.t("datox.report.exportDetails"),
        exportPreparing: language.t("datox.report.exportPreparing"),
        exportPreparingHint: language.t("datox.report.exportPreparingHint"),
        exportEmptySelection: language.t("datox.report.exportEmptySelection"),
        exportSnapshotHint: language.t("datox.report.exportSnapshotHint"),
        exportFailed: language.t("datox.report.exportFailed"),
        exportDraft: language.t("datox.report.exportDraft"),
        pdfPreview: language.t("datox.dashboard.pdf.preview"),
        pdfOrientation: language.t("datox.dashboard.pdf.orientation"),
        pdfMargin: language.t("datox.dashboard.pdf.margin"),
        pdfTextSize: language.t("datox.dashboard.pdf.textSize"),
        pdfScale: language.t("datox.dashboard.pdf.scale"),
        pdfGray: language.t("datox.dashboard.pdf.gray"),
        pdfAllRows: language.t("datox.dashboard.pdf.allRows"),
        pdfTableDensity: language.t("datox.dashboard.pdf.tableDensity"),
        pdfOriginal: language.t("datox.dashboard.pdf.original"),
        pdfCompact: language.t("datox.dashboard.pdf.compact"),
        pdfNormal: language.t("datox.dashboard.pdf.normal"),
        pdfLarge: language.t("datox.dashboard.pdf.large"),
        pdfAccent: language.t("datox.dashboard.pdf.accent"),
        pdfWidgetAdjustments: language.t("datox.dashboard.pdf.widgetAdjustments"),
        pdfWidgetHelp: language.t("datox.dashboard.pdf.widgetHelp"),
        pdfWidgetSelect: language.t("datox.dashboard.pdf.widgetSelect"),
        pdfWidgetTitleSize: language.t("datox.dashboard.pdf.widgetTitleSize"),
        pdfWidgetContentSize: language.t("datox.dashboard.pdf.widgetContentSize"),
        pdfKpiScale: language.t("datox.dashboard.pdf.kpiScale"),
        pdfWidgetReset: language.t("datox.dashboard.pdf.widgetReset"),
        logoAlt: language.t("lesha.logo.alt"),
        dashboardTitle: language.t("datox.dashboard.title"),
        createDashboard: language.t("datox.dashboard.create"),
        renameDashboard: language.t("datox.dashboard.renameDashboard"),
        deleteDashboard: language.t("datox.dashboard.deleteDashboard"),
        deleteDashboardConfirm: language.t("datox.dashboard.deleteDashboardConfirm"),
        preview: language.t("datox.dashboard.preview"),
        publishedStatus: language.t("datox.dashboard.publishedStatus"),
        unpublishedStatus: language.t("datox.dashboard.unpublishedStatus"),
        publishDashboard: language.t("datox.dashboard.publishDashboard"),
        unpublishDashboard: language.t("datox.dashboard.unpublishDashboard"),
        publishDashboardCopied: language.t("datox.dashboard.publishDashboardCopied"),
        openLink: language.t("datox.dashboard.openLink"),
        copyLink: language.t("datox.dashboard.copyLink"),
        copied: language.t("datox.dashboard.copied"),
        done: language.t("common.close"),
        clients: language.t("datox.dashboard.clients"),
        clientsDescription: language.t("datox.dashboard.clientsDescription"),
        client: language.t("datox.dashboard.client"),
        clientTable: language.t("datox.dashboard.clientTable"),
        clientColumn: language.t("datox.dashboard.clientColumn"),
        clientValues: language.t("datox.dashboard.clientValues"),
        noClientValues: language.t("datox.dashboard.noClientValues"),
        allClients: language.t("datox.dashboard.allClients"),
        column: language.t("datox.dashboard.column"),
        exportMenu: language.t("datox.dashboard.exportMenu"),
        exportPdf: language.t("datox.dashboard.exportPdf"),
        shareAccess: language.t("datox.dashboard.shareAccess"),
        reviewers: language.t("datox.dashboard.reviewers"),
        reviewerName: language.t("datox.dashboard.reviewerName"),
        reviewerEmail: language.t("datox.dashboard.reviewerEmail"),
        reports: language.t("datox.dashboard.reports"),
        status: language.t("datox.dashboard.reviewStatus"),
        notSent: language.t("datox.dashboard.notSent"),
        sent: language.t("datox.dashboard.sent"),
        duplicateEmail: language.t("datox.dashboard.duplicateEmail"),
        addAnotherReviewer: language.t("datox.dashboard.addAnotherReviewer"),
        reviewersPlaceholder: language.t("datox.dashboard.reviewersPlaceholder"),
        sendShareAccess: language.t("datox.dashboard.sendShareAccess"),
        sendingShareAccess: language.t("datox.dashboard.sendingShareAccess"),
        tidyLayout: language.t("datox.dashboard.tidyLayout"),
        renameTab: language.t("datox.dashboard.renameTab"),
        duplicateTab: language.t("datox.dashboard.duplicateTab"),
        deleteTab: language.t("datox.dashboard.deleteTab"),
        rename: language.t("datox.dashboard.rename"),
        name: language.t("datox.dashboard.nameField"),
        doubleClickRename: language.t("datox.dashboard.doubleClickRename"),
        saving: language.t("datox.dashboard.saving"),
        widgetsPanel: language.t("datox.dashboard.widgets"),
        data: language.t("datox.dashboard.data"),
        design: language.t("datox.dashboard.design"),
        searchWidgets: language.t("datox.dashboard.searchWidgets"),
        listView: language.t("datox.dashboard.listView"),
        gridView: language.t("datox.dashboard.gridView"),
        outputTable: language.t("datox.dashboard.dataset"),
        source: language.t("datox.dashboard.source"),
        rows: language.t("datox.dashboard.rows"),
        columns: language.t("datox.dashboard.columns"),
        tableMetadata: language.t("datox.dashboard.tableMetadata"),
        columnMetadata: language.t("datox.dashboard.columnMetadata"),
        tablePreview: language.t("datox.dashboard.tablePreview"),
        notAssessed: language.t("datox.risk.indicator.unassessed"),
        closePanel: language.t("datox.dashboard.closePanel"),
        openPanel: language.t("datox.dashboard.openPanel"),
        panel: language.t("datox.dashboard.panel"),
        newTabName: language.t("datox.dashboard.newTabName"),
        widgetLibrary: language.t("datox.dashboard.widgetLibrary"),
        dashboardName: language.t("datox.dashboard.name"),
        dashboardNamePlaceholder: language.t("datox.dashboard.namePlaceholder"),
        defaultTabName: language.t("datox.dashboard.defaultTabName"),
        emptyCanvasTitle: language.t("datox.dashboard.emptyCanvasTitle"),
        emptyCanvasDescription: language.t("datox.dashboard.emptyCanvasDescription"),
        accentColor: language.t("datox.dashboard.accentColor"),
        selectedWidgetTitle: language.t("datox.dashboard.selectedWidgetTitle"),
        textContent: language.t("datox.dashboard.textContent"),
        titleContent: language.t("datox.dashboard.titleContent"),
        editText: language.t("datox.dashboard.editText"),
        widgetDataset: language.t("datox.dashboard.widgetDataset"),
        metricColumn: language.t("datox.dashboard.metricColumn"),
        summarize: language.t("datox.dashboard.summarize"),
        aggregation: language.t("datox.dashboard.aggregation"),
        aggregationSum: language.t("datox.dashboard.aggregationSum"),
        aggregationAverage: language.t("datox.dashboard.aggregationAverage"),
        aggregationMin: language.t("datox.dashboard.aggregationMin"),
        aggregationMax: language.t("datox.dashboard.aggregationMax"),
        aggregationCount: language.t("datox.dashboard.aggregationCount"),
        aggregationFirst: language.t("datox.dashboard.aggregationFirst"),
        aggregationLast: language.t("datox.dashboard.aggregationLast"),
        labelColumn: language.t("datox.dashboard.labelColumn"),
        editWidget: language.t("datox.dashboard.editWidget"),
        renameWidget: language.t("datox.dashboard.renameWidget"),
        editData: language.t("datox.dashboard.editData"),
        editDesign: language.t("datox.dashboard.editDesign"),
        exportTable: language.t("datox.dashboard.exportTable"),
        fullView: language.t("datox.dashboard.fullView"),
        widgetType: language.t("datox.dashboard.widgetType"),
        sampleData: language.t("datox.dashboard.sampleData"),
        filter: language.t("datox.dashboard.filter"),
        addFilter: language.t("datox.dashboard.addFilter"),
        noFiltersApplied: language.t("datox.dashboard.noFiltersApplied"),
        filterContains: language.t("datox.dashboard.filterContains"),
        filterEquals: language.t("datox.dashboard.filterEquals"),
        filterNotEmpty: language.t("datox.dashboard.filterNotEmpty"),
        filterTargets: language.t("datox.dashboard.filterTargets"),
        sort: language.t("datox.dashboard.sort"),
        noSort: language.t("datox.dashboard.noSort"),
        sortAscending: language.t("datox.dashboard.sortAscending"),
        sortDescending: language.t("datox.dashboard.sortDescending"),
        rowLimit: language.t("datox.dashboard.rowLimit"),
        noRowLimit: language.t("datox.dashboard.noRowLimit"),
        chartData: language.t("datox.dashboard.chartData"),
        groupBy: language.t("datox.dashboard.groupBy"),
        groupByHelp: language.t("datox.dashboard.groupByHelp"),
        dataStreams: language.t("datox.dashboard.dataStreams"),
        dataStreamsHelp: language.t("datox.dashboard.dataStreamsHelp"),
        dataStreamDisplayNames: language.t("datox.dashboard.dataStreamDisplayNames"),
        showTop: language.t("datox.dashboard.showTop"),
        by: language.t("datox.dashboard.by"),
        order: language.t("datox.dashboard.order"),
        logo: language.t("datox.dashboard.logo"),
        uploadLogo: language.t("datox.dashboard.uploadLogo"),
        headerColor: language.t("datox.dashboard.headerColor"),
        cornerRadius: language.t("datox.dashboard.cornerRadius"),
        radiusSquare: language.t("datox.dashboard.radiusSquare"),
        radiusMedium: language.t("datox.dashboard.radiusMedium"),
        radiusLarge: language.t("datox.dashboard.radiusLarge"),
        fontSize: language.t("datox.dashboard.fontSize"),
        fontWeight: language.t("datox.dashboard.fontWeight"),
        textColor: language.t("datox.dashboard.textColor"),
        textAlign: language.t("datox.dashboard.textAlign"),
        verticalAlign: language.t("datox.dashboard.verticalAlign"),
        alignLeft: language.t("datox.dashboard.alignLeft"),
        alignCenter: language.t("datox.dashboard.alignCenter"),
        alignRight: language.t("datox.dashboard.alignRight"),
        alignTop: language.t("datox.dashboard.alignTop"),
        alignMiddle: language.t("datox.dashboard.alignMiddle"),
        alignBottom: language.t("datox.dashboard.alignBottom"),
        fontCompact: language.t("datox.dashboard.fontCompact"),
        fontNormal: language.t("datox.dashboard.fontNormal"),
        fontLarge: language.t("datox.dashboard.fontLarge"),
        headingFont: language.t("datox.dashboard.headingFont"),
        bodyFont: language.t("datox.dashboard.bodyFont"),
        widgetShadows: language.t("datox.dashboard.widgetShadows"),
        lightGrayBackground: language.t("datox.dashboard.lightGrayBackground"),
        tableDensity: language.t("datox.dashboard.tableDensity"),
        showTableHeader: language.t("datox.dashboard.showTableHeader"),
        stripedRows: language.t("datox.dashboard.stripedRows"),
        headerFill: language.t("datox.dashboard.headerFill"),
        filterDisplay: language.t("datox.dashboard.filterDisplay"),
        filterDisplayChips: language.t("datox.dashboard.filterDisplayChips"),
        filterDisplayList: language.t("datox.dashboard.filterDisplayList"),
        filterDisplayDropdown: language.t("datox.dashboard.filterDisplayDropdown"),
        allowMultipleValues: language.t("datox.dashboard.allowMultipleValues"),
        chartOrientation: language.t("datox.dashboard.chartOrientation"),
        chartType: language.t("datox.dashboard.chartType"),
        chartSection: language.t("datox.dashboard.chartSection"),
        labelsSection: language.t("datox.dashboard.labelsSection"),
        donutChart: language.t("datox.dashboard.donutChart"),
        showValuesNextToLabels: language.t("datox.dashboard.showValuesNextToLabels"),
        vertical: language.t("datox.dashboard.vertical"),
        horizontal: language.t("datox.dashboard.horizontal"),
        showLegend: language.t("datox.dashboard.showLegend"),
        showValueLabels: language.t("datox.dashboard.showValueLabels"),
        showAxisLabels: language.t("datox.dashboard.showAxisLabels"),
        showGridLines: language.t("datox.dashboard.showGridLines"),
        distinctBarColors: language.t("datox.dashboard.distinctBarColors"),
        stackSeries: language.t("datox.dashboard.stackSeries"),
        xAxisLabel: language.t("datox.dashboard.xAxisLabel"),
        yAxisLabel: language.t("datox.dashboard.yAxisLabel"),
        valueFormat: language.t("datox.dashboard.valueFormat"),
        numberFormat: language.t("datox.dashboard.numberFormat"),
        integerFormat: language.t("datox.dashboard.integerFormat"),
        percentFormat: language.t("datox.dashboard.percentFormat"),
        decimalPlaces: language.t("datox.dashboard.decimalPlaces"),
        duplicateWidget: language.t("datox.dashboard.duplicateWidget"),
        deleteWidget: language.t("datox.dashboard.deleteWidget"),
        copySuffix: language.t("datox.dashboard.copySuffix"),
        metricFallback: language.t("datox.dashboard.metricFallback"),
        itemLabel: language.t("datox.dashboard.itemLabel"),
        valueLabel: language.t("datox.dashboard.valueLabel"),
        textPlaceholder: language.t("datox.dashboard.textPlaceholder"),
        portrait: language.t("datox.dashboard.portrait"),
        landscape: language.t("datox.dashboard.landscape"),
        undo: language.t("datox.dashboard.undo"),
        redo: language.t("datox.dashboard.redo"),
        drag: language.t("datox.dashboard.drag"),
        more: language.t("datox.dashboard.more"),
        widgetCount: language.t("datox.dashboard.widgetCount"),
        tabCount: language.t("datox.dashboard.tabCount"),
        allChangesSaved: language.t("datox.dashboard.allChangesSaved"),
        somethingWentWrong: language.t("datox.dashboard.somethingWentWrong"),
        cancel: language.t("common.cancel"),
        widgets: {
          header: language.t("datox.dashboard.widget.header"),
          text: language.t("datox.dashboard.widget.text"),
          title: language.t("datox.dashboard.widget.title"),
          kpi: language.t("datox.dashboard.widget.kpi"),
          table: language.t("datox.dashboard.widget.table"),
          freeTable: language.t("datox.dashboard.widget.freeTable"),
          bar: language.t("datox.dashboard.widget.bar"),
          line: language.t("datox.dashboard.widget.line"),
          scatter: language.t("datox.dashboard.widget.scatter"),
          pie: language.t("datox.dashboard.widget.pie"),
          heatmap: language.t("datox.dashboard.widget.heatmap"),
          multiSeries: language.t("datox.dashboard.widget.multiSeries"),
          treemap: language.t("datox.dashboard.widget.treemap"),
          funnel: language.t("datox.dashboard.widget.funnel"),
          filter: language.t("datox.dashboard.widget.filter"),
        },
      }}
    /></Show>}>
      <EmptyDashboardState onCreate={openCreateDashboardDialog} disabled={store.saving} />
    </Show>
    </div>
    <Show keyed when={store.source}>{(source) => <Suspense fallback={<p role="status">{language.t("datox.source.loading")}</p>}><DashboardSourceDialog projectID={props.projectID} reference={source.reference} expectedRevision={source.expectedRevision} onClose={() => setStore("source", undefined)} /></Suspense>}</Show>
    </div>
  )

  return (
    <div class="flex h-full min-h-0 flex-col bg-white text-[#101828]">
      <div class="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-[#e4e7ec] bg-white px-5">
        <div class="flex min-w-0 items-center gap-2">
          <h2 class="text-[15px] font-semibold text-[#101828]">{language.t("datox.dashboard.title")}</h2>
          <select
            class="h-9 min-w-64 rounded-md border border-[#d0d5dd] bg-white px-3 text-[13px] text-[#344054] outline-none"
            value={store.selectedDashboardID}
            onChange={(event) => setStore("selectedDashboardID", event.currentTarget.value)}
          >
            <For each={dashboards() ?? []}>{(dashboard) => <option value={dashboard.id}>{dashboard.name}</option>}</For>
          </select>
          <button class="rounded-md border border-[#d0d5dd] bg-white px-3 py-2 text-[12px] font-semibold text-[#344054] shadow-sm hover:bg-[#f9fafb]" onClick={openCreateDashboardDialog} disabled={store.saving}>
            {language.t("datox.dashboard.create")}
          </button>
        </div>
        <button class="rounded-md bg-[#2f80ed] px-4 py-2 text-[12px] font-semibold text-white shadow-sm disabled:opacity-60" onClick={saveDashboard} disabled={!activeDashboard() || store.saving}>
          {store.saving ? language.t("datox.dashboard.saving") : language.t("datox.dashboard.save")}
        </button>
      </div>
      <Show when={store.error}>
        <div class="border-b border-red-500/30 bg-red-500/10 px-3 py-2 text-12-regular text-red-200">{store.error}</div>
      </Show>
      <Show when={dashboards.loading} fallback={
        <Show when={activeDashboard()} fallback={<EmptyDashboardState onCreate={openCreateDashboardDialog} disabled={store.saving} />}>
          <div class="grid min-h-0 flex-1 grid-cols-[320px_minmax(0,1fr)] overflow-hidden bg-[#f7f9fc]">
            <aside class="flex min-h-0 flex-col border-r border-[#e4e7ec] bg-white p-4">
              <div class="mb-4 rounded-lg bg-[#f0f2f5] p-1">
                <div class="grid grid-cols-2 gap-1">
                  <div class="rounded-md bg-white px-3 py-2 text-center text-[13px] font-semibold text-[#101828] shadow-sm">{language.t("datox.dashboard.widgets")}</div>
                  <div class="rounded-md px-3 py-2 text-center text-[13px] font-semibold text-[#667085]">{language.t("datox.dashboard.design")}</div>
                </div>
              </div>
              <div class="mb-3 flex items-center gap-2">
                <div class="relative flex-1">
                  <span class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-[#98a2b3]">/</span>
                  <input
                    class="h-9 w-full rounded-md border border-[#d0d5dd] bg-white pl-8 pr-3 text-[13px] text-[#344054] outline-none placeholder:text-[#98a2b3]"
                    placeholder={language.t("datox.dashboard.searchWidgets")}
                    value={store.paletteSearch}
                    onInput={(event) => setStore("paletteSearch", event.currentTarget.value)}
                  />
                </div>
                <div class="flex h-9 items-center gap-1 rounded-md border border-[#d0d5dd] bg-[#f2f4f7] p-1">
                  <span class="rounded bg-white px-2 py-1 text-[11px] font-semibold text-[#344054] shadow-sm">=</span>
                  <span class="px-2 py-1 text-[11px] font-semibold text-[#667085]">::</span>
                </div>
              </div>
              <label class="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[#98a2b3]" for="datox-dashboard-dataset">{language.t("datox.dashboard.dataset")}</label>
              <select
                id="datox-dashboard-dataset"
                class="mb-4 h-9 rounded-md border border-[#d0d5dd] bg-white px-3 text-[13px] text-[#344054] outline-none"
                value={store.selectedDatasetID}
                onChange={(event) => setStore("selectedDatasetID", event.currentTarget.value)}
              >
                <For each={datasetOptions()}>{(dataset) => <option value={dataset.id}>{dataset.name}</option>}</For>
              </select>
              <div class="mb-3 text-[12px] font-bold uppercase tracking-wide text-[#98a2b3]">{language.t("datox.dashboard.widgetLibrary")}</div>
              <div class="min-h-0 flex-1 space-y-1 overflow-auto pr-1">
                <For each={filteredPaletteItems()}>
                  {(item) => <WidgetButton type={item.type} label={item.label} onClick={() => addWidget(item.type)} disabled={!store.selectedDatasetID && dataBackedWidgetTypes.has(item.type)} />}
                </For>
              </div>
              <div class="mt-6 rounded-lg border border-[#e4e7ec] bg-[#f9fafb] p-3">
                <div class="mb-2 text-[13px] font-semibold text-[#101828]">{language.t("datox.dashboard.design")}</div>
                <label class="mb-1 block text-[11px] font-semibold text-[#667085]" for="datox-dashboard-title">{language.t("datox.dashboard.name")}</label>
                <input
                  id="datox-dashboard-title"
                  class="h-9 w-full rounded-md border border-[#d0d5dd] bg-white px-3 text-[13px] text-[#344054] outline-none"
                  value={store.config.title}
                  onInput={(event) => setStore("config", "title", event.currentTarget.value)}
                />
              </div>
            </aside>
            <main class="min-h-0 overflow-auto bg-[#f3f6fb] bg-[radial-gradient(circle_at_1px_1px,rgba(102,112,133,0.26)_1px,transparent_0)] [background-size:20px_20px] p-8">
              <div class="mx-auto rounded-[3px] border border-[#e4e7ec] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.06),0_12px_28px_rgba(16,24,40,0.10)]" style={{ width: `${store.config.canvas.width}px`, "min-height": `${store.config.canvas.height}px`, position: "relative" }}>
                <For each={activeTab().widgets} fallback={<CanvasEmpty />}>
                  {(widget) => <DashboardWidgetView widget={widget} table={tableForDataset(widgetTables(), widget.data?.dataset_id)} accent={store.config.design.accent} onRemove={() => removeWidget(widget.id)} />}
                </For>
              </div>
            </main>
          </div>
        </Show>
      }>
        <div class="flex flex-1 items-center justify-center text-13-regular text-v2-text-text-muted">{language.t("datox.dashboard.loading")}</div>
      </Show>
    </div>
  )
}

function DialogDatoxDashboardForm(props: {
  title: string
  submitLabel: string
  onSubmit: (name: string) => void
}) {
  const language = useLanguage()
  const dialog = useDialog()
  const [store, setStore] = createStore({ name: "" })
  const submit = (event: SubmitEvent) => {
    event.preventDefault()
    const name = store.name.trim()
    if (!name) return
    dialog.close()
    props.onSubmit(name)
  }

  return (
    <Dialog fit>
      <form class="contents" onSubmit={submit}>
        <DialogHeader>
          <DialogTitle>{props.title}</DialogTitle>
        </DialogHeader>
        <DividerV2 />
        <DialogBody class="flex w-96 flex-col gap-4 px-4 pt-4 pb-1">
          <Field>
            <Field.Label>{language.t("datox.dashboard.name")}</Field.Label>
            <TextInputV2
              autofocus
              appearance="large"
              class="!w-full"
              placeholder={language.t("datox.dashboard.namePlaceholder")}
              value={store.name}
              onInput={(event) => setStore("name", event.currentTarget.value)}
            />
          </Field>
        </DialogBody>
        <DialogFooter>
          <ButtonV2 type="button" variant="neutral" onClick={() => dialog.close()}>
            {language.t("common.cancel")}
          </ButtonV2>
          <ButtonV2 type="submit" variant="contrast" disabled={!store.name.trim()}>
            {props.submitLabel}
          </ButtonV2>
        </DialogFooter>
      </form>
    </Dialog>
  )
}

function EmptyDashboardState(props: { onCreate: () => void; disabled: boolean }) {
  const language = useLanguage()
  return (
    <div class="flex flex-1 items-center justify-center p-6 text-center">
      <div class="max-w-sm rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-6">
        <h3 class="text-16-semibold text-v2-text-text-base">{language.t("datox.dashboard.emptyTitle")}</h3>
        <p class="mt-2 text-13-regular text-v2-text-text-muted">{language.t("datox.dashboard.emptyDescription")}</p>
        <button class="mt-4 rounded-md bg-blue-600 px-3 py-2 text-13-medium text-white disabled:opacity-60" onClick={props.onCreate} disabled={props.disabled}>
          {language.t("datox.dashboard.create")}
        </button>
      </div>
    </div>
  )
}

function WidgetButton(props: { type: DashboardWidgetType; label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button class="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-[13px] text-[#344054] hover:bg-[#f2f4f7] disabled:cursor-not-allowed disabled:opacity-50" onClick={props.onClick} disabled={props.disabled}>
      <WidgetGlyph type={props.type} />
      {props.label}
    </button>
  )
}

function WidgetGlyph(props: { type: DashboardWidgetType }) {
  if (props.type === "header") return <span class="h-3 w-4 rounded-sm border border-[#c0c6d0]" />
  if (props.type === "text" || props.type === "title") return <span class="flex h-4 w-4 flex-col justify-center gap-1"><span class="h-px w-4 bg-[#c0c6d0]" /><span class="h-px w-3 bg-[#c0c6d0]" /></span>
  if (props.type === "kpi") return <span class="flex h-4 w-4 items-end gap-0.5"><span class="h-1.5 w-1 rounded-sm bg-[#c0c6d0]" /><span class="h-3 w-1 rounded-sm bg-[#c0c6d0]" /><span class="h-2 w-1 rounded-sm bg-[#c0c6d0]" /></span>
  if (props.type === "table" || props.type === "freeTable") return <span class="grid h-4 w-4 grid-cols-1 gap-0.5"><span class="rounded-sm border border-[#c0c6d0]" /><span class="rounded-sm border border-[#c0c6d0]" /></span>
  if (props.type === "pie") return <span class="h-4 w-4 rounded-full border-2 border-[#c0c6d0] border-r-transparent" />
  if (props.type === "filter") return <span class="h-0 w-0 border-l-[7px] border-r-[7px] border-t-[13px] border-l-transparent border-r-transparent border-t-[#c0c6d0]" />
  return <span class="flex h-4 w-4 items-end gap-0.5"><span class="h-2 w-1 rounded-sm bg-[#c0c6d0]" /><span class="h-3.5 w-1 rounded-sm bg-[#c0c6d0]" /><span class="h-1.5 w-1 rounded-sm bg-[#c0c6d0]" /></span>
}

function DashboardWidgetView(props: { widget: DashboardWidget; table?: OutputTable; accent: string; onRemove: () => void }) {
  const language = useLanguage()
  const headerWidget = createMemo(() => props.widget.type === "header")
  return (
    <section class={headerWidget() ? "absolute overflow-hidden rounded-none bg-[#101828] text-white" : "absolute overflow-hidden rounded-xl border border-[#e4e7ec] bg-white text-[#101828] shadow-[0_1px_2px_rgba(16,24,40,0.05)]"} style={{ left: `${props.widget.layout.x}px`, top: `${props.widget.layout.y}px`, width: `${props.widget.layout.w}px`, height: `${props.widget.layout.h}px` }}>
      <div class={headerWidget() ? "absolute right-3 top-2 z-10" : "flex items-center justify-between px-4 pb-1 pt-4"}>
        <Show when={!headerWidget()}>
          <h3 class="truncate text-[16px] font-bold text-[#101828]">{props.widget.title}</h3>
        </Show>
        <button class={headerWidget() ? "rounded px-2 py-1 text-xs text-white/80 hover:bg-white/10" : "rounded px-2 py-1 text-xs text-[#667085] hover:bg-[#f2f4f7]"} aria-label={language.t("datox.dashboard.removeWidget")} onClick={props.onRemove}>...</button>
      </div>
      <div class={headerWidget() ? "h-full overflow-auto" : "h-[calc(100%-45px)] overflow-auto px-4 pb-4 pt-1"}>
        <Show when={props.widget.type === "header"}>
          <div class="flex h-full items-center px-8 text-[34px] font-extrabold tracking-tight text-white">{props.widget.title}</div>
        </Show>
        <Show when={props.widget.type === "text"}>
          <p class="text-sm leading-6 text-slate-600">{props.widget.text}</p>
        </Show>
        <Show when={props.widget.type === "title"}>
          <h2 class="text-2xl font-bold text-slate-950">{props.widget.title}</h2>
        </Show>
        <Show when={props.widget.type === "kpi"}>
          <KpiWidget table={props.table} metric={props.widget.data?.metric} accent={props.accent} />
        </Show>
        <Show when={props.widget.type === "table" || props.widget.type === "freeTable"}>
          <TableWidget table={props.table} columns={props.widget.data?.columns} accent={props.accent} />
        </Show>
        <Show when={props.widget.type === "bar"}>
          <BarWidget table={props.table} metric={props.widget.data?.metric} label={props.widget.data?.label} accent={props.accent} />
        </Show>
        <Show when={props.widget.type === "line" || props.widget.type === "multiSeries"}>
          <LineWidget table={props.table} metric={props.widget.data?.metric} label={props.widget.data?.label} accent={props.accent} />
        </Show>
        <Show when={props.widget.type === "scatter"}>
          <ScatterWidget table={props.table} metric={props.widget.data?.metric} label={props.widget.data?.label} accent={props.accent} />
        </Show>
        <Show when={props.widget.type === "pie" || props.widget.type === "treemap" || props.widget.type === "funnel"}>
          <RankedWidget table={props.table} metric={props.widget.data?.metric} label={props.widget.data?.label} accent={props.accent} variant={props.widget.type} />
        </Show>
        <Show when={props.widget.type === "heatmap"}>
          <HeatmapWidget table={props.table} metric={props.widget.data?.metric} label={props.widget.data?.label} accent={props.accent} />
        </Show>
        <Show when={props.widget.type === "filter"}>
          <FilterWidget table={props.table} label={props.widget.data?.label} />
        </Show>
      </div>
    </section>
  )
}

function CanvasEmpty() {
  const language = useLanguage()
  return <div class="absolute inset-0 flex items-center justify-center text-sm text-slate-400">{language.t("datox.dashboard.canvasEmpty")}</div>
}

function KpiWidget(props: { table?: OutputTable; metric?: string; accent: string }) {
  const values = () => numericValues(props.table, props.metric)
  const total = () => values().reduce((sum, value) => sum + value, 0)
  return (
    <div>
      <div class="text-4xl font-bold" style={{ color: props.accent }}>{formatNumber(total())}</div>
      <div class="mt-2 text-sm text-slate-500">{props.metric}</div>
    </div>
  )
}

function TableWidget(props: { table?: OutputTable; columns?: string[]; accent: string }) {
  const columns = () => props.columns?.length ? props.columns : props.table?.headers ?? []
  return (
    <table class="w-full border-collapse text-left text-sm">
      <thead style={{ background: props.accent, color: "white" }}>
        <tr><For each={columns()}>{(column) => <th class="px-3 py-2 font-semibold">{column}</th>}</For></tr>
      </thead>
      <tbody>
        <For each={(props.table?.rows ?? []).slice(0, 12)}>
          {(row) => <tr class="border-b border-slate-100"><For each={columns()}>{(column) => <td class="px-3 py-2">{String(row[column] ?? "")}</td>}</For></tr>}
        </For>
      </tbody>
    </table>
  )
}

function BarWidget(props: { table?: OutputTable; metric?: string; label?: string; accent: string }) {
  const rows = () => (props.table?.rows ?? []).slice(0, 8)
  const max = () => Math.max(1, ...numericValues(props.table, props.metric))
  return (
    <div class="space-y-3">
      <For each={rows()}>
        {(row) => {
          const value = Number(row[props.metric ?? ""] ?? 0)
          const width = `${Math.max(4, Math.min(100, (value / max()) * 100))}%`
          return <div><div class="mb-1 flex justify-between text-xs text-slate-500"><span>{String(row[props.label ?? ""] ?? "-")}</span><span>{formatNumber(value)}</span></div><div class="h-3 rounded bg-slate-100"><div class="h-3 rounded" style={{ width, background: props.accent }} /></div></div>
        }}
      </For>
    </div>
  )
}

function LineWidget(props: { table?: OutputTable; metric?: string; label?: string; accent: string }) {
  const rows = () => (props.table?.rows ?? []).slice(0, 12)
  const values = () => rows().map((row) => Number(row[props.metric ?? ""] ?? 0)).filter((value) => Number.isFinite(value))
  const max = () => Math.max(1, ...values())
  const points = () => values().map((value, index) => `${index * (320 / Math.max(1, values().length - 1))},${110 - (value / max()) * 90}`).join(" ")
  return (
    <div class="h-full">
      <svg viewBox="0 0 340 130" class="h-36 w-full overflow-visible">
        <polyline fill="none" stroke={props.accent} stroke-width="4" points={points()} />
        <For each={values()}>{(value, index) => <circle cx={index() * (320 / Math.max(1, values().length - 1))} cy={110 - (value / max()) * 90} r="4" fill={props.accent} />}</For>
      </svg>
      <div class="grid grid-cols-4 gap-2 text-[10px] text-slate-400">
        <For each={rows().slice(0, 4)}>{(row) => <span class="truncate">{String(row[props.label ?? ""] ?? "-")}</span>}</For>
      </div>
    </div>
  )
}

function ScatterWidget(props: { table?: OutputTable; metric?: string; label?: string; accent: string }) {
  const rows = () => (props.table?.rows ?? []).slice(0, 24)
  const values = () => rows().map((row) => Number(row[props.metric ?? ""] ?? 0)).filter((value) => Number.isFinite(value))
  const max = () => Math.max(1, ...values())
  return (
    <svg viewBox="0 0 340 160" class="h-full w-full rounded-lg bg-slate-50">
      <For each={values()}>{(value, index) => <circle cx={24 + (index() % 8) * 38} cy={140 - (value / max()) * 110} r="6" fill={props.accent} opacity="0.75" />}</For>
    </svg>
  )
}

function RankedWidget(props: { table?: OutputTable; metric?: string; label?: string; accent: string; variant: DashboardWidgetType }) {
  const rows = () => [...(props.table?.rows ?? [])]
    .map((row) => ({ label: String(row[props.label ?? ""] ?? "-"), value: Number(row[props.metric ?? ""] ?? 0) }))
    .filter((row) => Number.isFinite(row.value))
    .sort((first, second) => second.value - first.value)
    .slice(0, 6)
  const total = () => Math.max(1, rows().reduce((sum, row) => sum + row.value, 0))
  return (
    <div class="grid h-full grid-cols-2 gap-4">
      <div class="flex items-center justify-center">
        <div class="relative h-28 w-28 rounded-full" style={{ background: `conic-gradient(${props.accent} 0 70%, #dbe4f0 70% 100%)` }}>
          <div class="absolute inset-5 rounded-full bg-white" />
        </div>
      </div>
      <div class="space-y-2 overflow-auto">
        <For each={rows()}>
          {(row, index) => <div><div class="mb-1 flex justify-between text-xs text-slate-500"><span class="truncate">{row.label}</span><span>{formatNumber(row.value)}</span></div><div class="h-2 rounded bg-slate-100"><div class="h-2 rounded" style={{ width: `${Math.max(8, (row.value / total()) * 100)}%`, background: index() === 0 ? props.accent : "#93baf2" }} /></div></div>}
        </For>
      </div>
    </div>
  )
}

function HeatmapWidget(props: { table?: OutputTable; metric?: string; label?: string; accent: string }) {
  const values = () => numericValues(props.table, props.metric).slice(0, 36)
  const max = () => Math.max(1, ...values())
  return (
    <div class="grid grid-cols-6 gap-2">
      <For each={values()}>{(value) => <div class="aspect-square rounded" style={{ background: props.accent, opacity: Math.max(0.18, value / max()) }} />}</For>
    </div>
  )
}

function FilterWidget(props: { table?: OutputTable; label?: string }) {
  const values = () => [...new Set((props.table?.rows ?? []).map((row) => String(row[props.label ?? ""] ?? "-")).slice(0, 8))]
  return (
    <div class="flex flex-wrap gap-2">
      <For each={values()}>{(value) => <span class="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">{value}</span>}</For>
    </div>
  )
}

function defaultWidget(type: DashboardWidgetType, index: number, datasetID: string, metric: string | undefined, label: string | undefined, translate: ReturnType<typeof useLanguage>["t"]): DashboardWidget {
  const x = 40 + (index % 2) * 500
  const y = 40 + Math.floor(index / 2) * 250
  if (type === "header") return { id: widgetID(), type, title: translate("datox.dashboard.widget.header"), layout: { x: 40, y, w: 960, h: 120 } }
  if (type === "text") return { id: widgetID(), type, title: translate("datox.dashboard.widget.text"), text: translate("datox.dashboard.textPlaceholder"), layout: { x, y, w: 420, h: 180 } }
  if (type === "title") return { id: widgetID(), type, title: translate("datox.dashboard.widget.title"), layout: { x, y, w: 420, h: 140 } }
  if (type === "kpi") return { id: widgetID(), type, title: translate("datox.dashboard.widget.kpi"), layout: { x, y, w: 300, h: 180 }, data: { dataset_id: datasetID, metric } }
  if (type === "table" || type === "freeTable") return { id: widgetID(), type, title: translate(type === "freeTable" ? "datox.dashboard.widget.freeTable" : "datox.dashboard.widget.table"), layout: { x: 40, y, w: 760, h: 260 }, data: { dataset_id: datasetID, columns: [] } }
  return { id: widgetID(), type, title: translate(widgetLabelKey(type)), layout: { x, y, w: 420, h: 260 }, data: { dataset_id: datasetID, metric, label } }
}

function widgetLabelKey(type: DashboardWidgetType) {
  if (type === "line") return "datox.dashboard.widget.line"
  if (type === "scatter") return "datox.dashboard.widget.scatter"
  if (type === "pie") return "datox.dashboard.widget.pie"
  if (type === "heatmap") return "datox.dashboard.widget.heatmap"
  if (type === "multiSeries") return "datox.dashboard.widget.multiSeries"
  if (type === "treemap") return "datox.dashboard.widget.treemap"
  if (type === "funnel") return "datox.dashboard.widget.funnel"
  if (type === "filter") return "datox.dashboard.widget.filter"
  return "datox.dashboard.widget.bar"
}

function parseDashboardConfig(dashboard: DatoxDashboard): DashboardConfig {
  const value = dashboard.draft_config
  if (isDashboardConfig(value)) return value
  return defaultDashboardConfig(dashboard.name, dashboard.name)
}

function isDashboardConfig(value: unknown): value is DashboardConfig {
  return isRecord(value) && typeof value.title === "string" && isRecord(value.canvas) && Array.isArray(value.tabs) && isRecord(value.design)
}

function defaultDashboardConfig(title: string, tabName = title): DashboardConfig {
  return { title, canvas: { orientation: "landscape", width: 1123, height: 794 }, tabs: [{ id: "tab-1", name: tabName, widgets: [] }], design: { theme: "light", accent: "#2f80ed" } }
}

function transformationOutputTables(transformation: DatoxTransformation): OutputTable[] {
  const raw = transformation.output_json.output_tables
  if (Array.isArray(raw)) return raw.flatMap((item, index) => isRecord(item) && item.visibility !== "internal"
    ? normalizeTable(item, "").map((table) => ({ ...table, datasetID: `transformation:${transformation.id}:table:${index}` })) : [])
  return outputTables(transformation.output_json).map((table, index) => ({ ...table, datasetID: `transformation:${transformation.id}:table:${index}` }))
}

function outputTables(value: Record<string, unknown>): Array<Omit<OutputTable, "datasetID">> {
  return tablesFromCollection(value.output_tables).length ? tablesFromCollection(value.output_tables) : tablesFromCollection(value.tables)
}

function tablesFromCollection(value: unknown): Array<Omit<OutputTable, "datasetID">> {
  if (!Array.isArray(value)) return []
  return value.flatMap((item, index) => normalizeTable(item, `Output table ${index + 1}`))
}

function normalizeTable(value: unknown, fallbackName: string): Array<Omit<OutputTable, "datasetID">> {
  if (!isRecord(value)) return []
  const rowsValue = value.rows ?? value.data ?? value.values
  if (!Array.isArray(rowsValue)) return []
  const columns = value.columns ?? value.headers
  const explicitHeaders = Array.isArray(columns) ? columns.flatMap((column) => typeof column === "string" ? [column] : isRecord(column) && typeof column.name === "string" ? [column.name] : []) : []
  if (!rowsValue.length) {
    return explicitHeaders.length ? [{ name: stringValue(value.name) || fallbackName, headers: explicitHeaders, rows: [] }] : []
  }
  const rows = explicitHeaders.length && rowsValue.every(Array.isArray)
    ? rowsValue.map((row) => Object.fromEntries(explicitHeaders.map((header, index) => [header, cellValue(row[index])])))
    : normalizeRows(rowsValue)
  if (rows.length === 0) return []
  return [{ name: stringValue(value.name ?? value.table_name ?? value.title) || fallbackName, headers: explicitHeaders.length ? explicitHeaders : Object.keys(rows[0]), rows }]
}

function normalizeRows(rows: unknown[]): Array<Record<string, string | number | boolean | null>> {
  if (rows.every(isRecord)) {
    const headers = [...new Set(rows.flatMap((row) => Object.keys(row)))]
    return rows.map((row) => Object.fromEntries(headers.map((header) => [header, cellValue(row[header])])))
  }
  if (rows.every(Array.isArray)) {
    const headers = rows[0].map((value, index) => stringValue(value) || `Column ${index + 1}`)
    return rows.slice(1).map((row) => Object.fromEntries(headers.map((header, index) => [header, cellValue(row[index])])))
  }
  return []
}

function tableForDataset(tables: OutputTable[], datasetID: string | undefined) {
  return tables.find((table) => table.datasetID === datasetID)
}

function firstNumericColumn(table: OutputTable | undefined) {
  return table?.headers.find((header) => numericValues(table, header).length > 0)
}

function numericValues(table: OutputTable | undefined, metric: string | undefined) {
  if (!table || !metric) return []
  return table.rows.map((row) => Number(String(row[metric] ?? "").replace(/,/g, ""))).filter((value) => Number.isFinite(value))
}

function stringList(value: unknown) {
  if (!Array.isArray(value)) return []
  return value.map(stringValue).filter((item) => item.length > 0)
}

function stringValue(value: unknown) {
  return value == null ? "" : String(value)
}

function cellValue(value: unknown): string | number | boolean | null {
  if (value == null) return null
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value
  return String(value)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function formatNumber(value: number) {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)
}

function widgetID() {
  return `widget-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}
